/* eslint-disable unicorn/no-null */
import type {
  ApiResponse,
  AuthResponse,
  ChatState,
  Message,
  MessagesResponse,
  RemovalReason,
  Reply,
  TimeoutRequest,
  Watcher,
  WatchersResponse,
  WebSocketMessage,
  WebSocketStatus
} from '@livecontrol/scheduler/model';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseAdminChatProps {
  wsUrl: string;
  httpEndpoint: string;
  adminToken: string;
  eventId: string;
  clientId: string;
  autoConnect?: boolean;
}

interface FetchMessagesOptions {
  /*
   * Ask for the whole room instead of a page. Moderation needs it: a reply arrives over the socket
   * carrying only its parent's id, so a parent that was never loaded means the reply is dropped.
   */
  all?: boolean;
  lastEvaluatedKey?: string;
  timestamp?: number;
}

interface UseAdminChatReturn {
  messages: Message[];
  watchers: Watcher[];
  status: WebSocketStatus;
  error: string | undefined;
  isConnected: boolean;
  /*
   * The chat user the panel itself is connected as. Every moderator shares one hardcoded
   * identity, so it identifies the role and not the person — enough to keep it out of the
   * viewers list, not enough to attribute an action to anybody.
   */
  currentUserId?: string;
  connect: () => void;
  disconnect: () => void;
  fetchWatchers: (all?: boolean) => Promise<WatchersResponse>;
  fetchMessages: (options?: FetchMessagesOptions) => Promise<MessagesResponse>;
  timeoutUser: (userId: string, mins?: number) => Promise<ApiResponse>;
  /** Hides everything they wrote, for as long as the ban stands. Nothing is rewritten. */
  banUser: (userId: string) => Promise<ApiResponse>;
  /** Their content becomes visible again on its own: it was hidden, never removed. */
  unbanUser: (userId: string) => Promise<ApiResponse>;
  deleteMessage: (messageId: string, replyId?: string) => void;
  restoreMessage: (messageId: string, replyId?: string) => void;
}

interface ChatTokenStorage {
  eventId: string;
  token: string;
}

const CHAT_ADMIN_TOKENS_KEY = 'chat_admin_tokens';

const getStoredTokens = (): ChatTokenStorage[] => {
  try {
    const stored = localStorage.getItem(CHAT_ADMIN_TOKENS_KEY);

    return stored ? JSON.parse(stored) : [];
  } catch (error: unknown) {
    console.error('Error parsing stored tokens:', error);

    return [];
  }
};

/*
 * The subject of our own chat token. Decoded, not verified: it only decides whether a row in the
 * viewers list is the moderator themselves, and nothing that matters hangs off it — the server is
 * what enforces every moderation action.
 *
 * Read from the token rather than stored alongside it so it also works for the tokens already
 * cached in browsers from before this existed.
 */
const userIdFromToken = (token: string): string | undefined => {
  try {
    const [, payload] = token.split('.');

    if (!payload) {
      return undefined;
    }

    /* base64url differs from base64 in two characters, and atob only speaks the latter. */
    const json = atob(payload.replaceAll('-', '+').replaceAll('_', '/'));

    return (<{ id?: string }>JSON.parse(json)).id;
  } catch {
    return undefined;
  }
};

const getTokenForEvent = (eventId: string): string | null => {
  const tokens = getStoredTokens();
  const tokenData = tokens.find(t => t.eventId === eventId);

  return tokenData?.token ?? null;
};

const mergeReplies = (previous: Reply[] = [], incoming: Reply[] = []): Reply[] => {
  const byId = new Map(previous.map((reply) => [reply.pk, reply]));

  incoming.forEach((reply) => byId.set(reply.pk, reply));

  return [...byId.values()].sort((a, b) => a.timestamp - b.timestamp);
};

/*
 * Folds a full snapshot from the server into what is already in memory instead of replacing it.
 *
 * The snapshot and the socket race on connect: `fetchMessages` is fired the moment the socket
 * opens and the auth response carries a second snapshot, so anything that arrived in between used
 * to be wiped when the slower of the two resolved. Replies are merged per message for the same
 * reason — a snapshot taken before a reply landed would otherwise erase it.
 */
const mergeMessages = (previous: Message[], incoming: Message[]): Message[] => {
  const byId = new Map(previous.map((message) => [message.pk, message]));

  incoming.forEach((message) => {
    const existing = byId.get(message.pk);

    byId.set(
      message.pk,
      existing
        ? { ...message, replies: mergeReplies(existing.replies, message.replies) }
        : message
    );
  });

  return [...byId.values()].sort((a, b) => a.timestamp - b.timestamp);
};

const saveTokenForEvent = (eventId: string, token: string): void => {
  const tokens = getStoredTokens();
  const existingIndex = tokens.findIndex(t => t.eventId === eventId);

  if (existingIndex >= 0) {
    tokens[existingIndex].token = token;
  } else {
    tokens.push({ eventId, token });
  }

  localStorage.setItem(CHAT_ADMIN_TOKENS_KEY, JSON.stringify(tokens));
};


export const useAdminChat = ({
  wsUrl,
  httpEndpoint,
  adminToken,
  eventId,
  clientId,
  autoConnect = true
}: UseAdminChatProps): UseAdminChatReturn => {
  const [state, setState] = useState<ChatState>({
    messages: [],
    watchers: [],
    status: 'disconnected',
    error: undefined
  });

  const ws = useRef<WebSocket | null>(null);
  const reconnectTimeout = useRef<NodeJS.Timeout>();
  const heartbeatInterval = useRef<NodeJS.Timeout>();
  const watchersRefreshInterval = useRef<NodeJS.Timeout>();

  const chatAuthToken = useRef<string | undefined>(undefined);

  /*
   * Every moderator registers as the same hardcoded 'Chat Admin', so this is not "me" so much as
   * "the moderator identity" — enough to keep it out of the viewers list, which is what it is for.
   */
  const [currentUserId, setCurrentUserId] = useState<string>();

  // Register Admin
  const getOrRegisterChatToken = useCallback(async (): Promise<string> => {
    const storedToken = getTokenForEvent(eventId);

    if (storedToken) {
      // console.log('Chat admin token from localStorage');

      return storedToken;
    }

    try {
      const resp = await fetch(`${httpEndpoint}/user/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: 'Chat',
          last_name: 'Admin',
          email: 'admin@livecontrol.io',
          client_id: clientId,
          room_id: eventId
        })
      });

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const data = await resp.json();

      if (!data?.body?.user?.token) {
        throw new Error('No se recibió token en el registro');
      }

      const token = <string>data.body.user.token;

      saveTokenForEvent(eventId, token);

      // localStorage.setItem(CHAT_ADMIN_TOKEN_KEY, token);

      return token;
    } catch (error: unknown) {
      console.error('Error registering Chat Admin:', error);

      throw error;
    }
  }, [httpEndpoint, clientId, eventId]);

  // Update messages
  const updateMessages = useCallback((messages: Message[]) => {
    setState((prev) => ({ ...prev, messages: mergeMessages(prev.messages, messages) }));
  }, []);

  // Update Watchers
  const updateWatchers = useCallback((watchers: Watcher[]) => {
    setState((prev) => ({ ...prev, watchers }));
  }, []);

  // Update connection Status
  const updateStatus = useCallback(
    (status: WebSocketStatus, error: string | undefined = undefined) => {
      setState((prev) => ({ ...prev, status, error }));
    },
    []
  );

  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
    }

    if (heartbeatInterval.current) {
      clearInterval(heartbeatInterval.current);
    }

    if (watchersRefreshInterval.current) {
      clearInterval(watchersRefreshInterval.current);
    }

    ws.current?.close();
    ws.current = null;
    updateStatus('disconnected');
  }, [updateStatus]);

  // Get watchers
  const fetchWatchers = useCallback(
    async (all: boolean = true) => {
      try {
        const url = `${httpEndpoint}/client/${clientId}/room/${eventId}/viewers${all ? '?all=true' : ''}`;

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        });

        const data: WatchersResponse = await response.json();

        if (data.success) {
          updateWatchers(data.body.watchers);
        }

        return data;
      } catch (error: unknown) {
        console.error('Error fetching watchers:', error);

        throw error;
      }
    },
    [httpEndpoint, eventId, updateWatchers]
  );

  // Get messages
  const fetchMessages = useCallback(
    async ({ all, lastEvaluatedKey, timestamp }: FetchMessagesOptions = {}) => {
      try {
        let url = `${httpEndpoint}/client/${clientId}/room/${eventId}/messages`;
        const params = new URLSearchParams();

        if (all) {
          params.append('all', 'true');
        }

        if (lastEvaluatedKey) {
          params.append('last_evaluated_key', lastEvaluatedKey);
        }

        if (timestamp) {
          params.append('timestamp', timestamp.toString());
        }

        if (params.toString()) {
          url += `?${params.toString()}`;
        }

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        });

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const data: MessagesResponse = await response.json();

        if (data.success) {
          updateMessages(data.body.messages);

          /*
           * With `all` this is the server's safety backstop, not a page boundary: the room was too
           * big to hand over in one go and what we hold is incomplete.
           */
          if (all && data.body.has_more) {
            // eslint-disable-next-line no-console -- silent truncation is worse than a lint error
            console.warn('Chat history truncated by the server; some messages are missing');
          }
        }

        return data;
      } catch (error: unknown) {
        console.error('Error fetching messages:', error);

        throw error;
      }
    },
    [httpEndpoint, eventId, updateMessages]
  );

  // Manage websokect incoming messages
  const handleWebSocketMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const data: AuthResponse | WebSocketMessage = JSON.parse(event.data);

        // auht response
        if ('body' in data && 'messages' in data.body) {
          // console.log('Auth response - loading messages:', data.body);
          updateMessages(data.body.messages);
          updateStatus('connected');

          return;
        }

        const wsMsg = <WebSocketMessage>data;

        /*
         * A ban says nothing about any single message: it flips the person, and the readers derive
         * the rest. The watchers list is refreshed on a timer anyway, but that is up to 30 seconds
         * of a moderator looking at a stale row.
         */
        if (wsMsg.action === 'ban' || wsMsg.action === 'unban') {
          const bannedAt = wsMsg.action === 'ban' ? wsMsg.timestamp ?? Date.now() : undefined;

          setState((prev) => ({
            ...prev,
            watchers: prev.watchers.map((watcher) =>
              watcher.id === wsMsg.id ? { ...watcher, banned_at: bannedAt } : watcher
            )
          }));

          return;
        }


        /*
         * Delete and restore are the same write with the flag flipped, so they share a branch.
         * A ban or an unban that takes content with it fans out as one of these per item, which is
         * why neither needs a case of its own here.
         */
        if (wsMsg.action === 'delete' || wsMsg.action === 'restore') {
          const deleted = wsMsg.action === 'delete';

          /* Restoring clears the reason: what comes back looks like it was never taken down. */
          const removedBy = deleted ? wsMsg.removedBy : undefined;

          if (wsMsg.objectType === 'CHAT_MESSAGE') {
            setState((prev) => ({
              ...prev,
              messages: prev.messages.map((msg) =>
                msg.pk === wsMsg.id ? { ...msg, deleted, removedBy } : msg
              )
            }));
          } else if (wsMsg.objectType === 'CHAT_REPLY') {
            setState((prev) => ({
              ...prev,
              messages: prev.messages.map((msg) => {
                if (msg.pk === wsMsg.messageId) {
                  return {
                    ...msg,
                    replies: msg.replies?.map((r) =>
                      r.pk === wsMsg.id ? { ...r, deleted, removedBy } : r
                    )
                  };
                }

                return msg;
              })
            }));
          }

          return;
        }

        // update
        if (wsMsg.action === 'update') {
          if (wsMsg.objectType === 'CHAT_MESSAGE') {
            // console.log('✏️ Admin: Update message action received');

            setState((prev) => ({
              ...prev,
              messages: prev.messages.map((msg) =>
                msg.pk === wsMsg.id 
                  ? { ...msg, message: wsMsg.content!}
                  : msg
              )
            }));
          } else if (wsMsg.objectType === 'CHAT_REPLY') {
            // console.log('✏️ Admin: Update reply action received');

            setState((prev) => ({
              ...prev,
              messages: prev.messages.map((msg) => {
                if (msg.pk === wsMsg.messageId) {
                  return {
                    ...msg,
                    replies: msg.replies?.map((r) =>
                      r.pk === wsMsg.id 
                        ? { ...r, reply: wsMsg.content! } 
                        : r
                    )
                  };
                }

                return msg;
              })
            }));
          }

          return;
        }

        // New message
        if (wsMsg.action === 'message' && wsMsg.objectType === 'CHAT_MESSAGE') {
          const newMessage: Message = {
            pk: wsMsg.id!,
            sk: wsMsg.senderId!,
            senderId: wsMsg.senderId!,
            senderHue: wsMsg.senderHue!,
            roomId: wsMsg.roomId!,
            message: wsMsg.message!,
            timestamp: wsMsg.timestamp!,
            senderFirst: wsMsg.username!.trim(),
            senderLast: '',
            deleted: wsMsg.deleted ?? false,
          };

          setState((prev) => ({
            ...prev,
            messages: [...prev.messages, newMessage]
          }));
        }

        // new response
        if (wsMsg.action === 'reply' && wsMsg.objectType === 'CHAT_REPLY') {
          setState((prev) => {
            /*
             * A reply only carries its parent's id, so it has nowhere to go if that parent is not
             * loaded — and it is then lost in silence. The initial fetch asks for the whole room
             * precisely so this cannot happen; say so out loud if it does anyway.
             */
            if (!prev.messages.some((msg) => msg.pk === wsMsg.messageId)) {
              // eslint-disable-next-line no-console -- the whole point is that it stops being silent
              console.warn('Dropped a reply whose parent is not loaded:', wsMsg.messageId);
            }

            return {
              ...prev,
              messages: prev.messages.map((msg) => {
                if (msg.pk === wsMsg.messageId) {
                  const newReply = {
                    senderId: wsMsg.senderId!,
                    senderFirst: wsMsg.username!.trim(),
                    senderHue: wsMsg.senderHue!,
                    messageId: wsMsg.messageId!,
                    senderLast: '',
                    pk: wsMsg.id!,
                    reply: wsMsg.reply!,
                    timestamp: wsMsg.timestamp!,
                    deleted: wsMsg.deleted ?? false
                  };

                  return {
                    ...msg,
                    replies: [...(msg.replies ?? []), newReply]
                  };
                }

                return msg;
              })
            };
          });
        }

        // new reaction
        if (wsMsg.objectType === 'CHAT_REACTION') {
          setState((prev) => ({
            ...prev,
            messages: prev.messages.map((msg) => {
              if (msg.pk === wsMsg.messageId) {
                const existingReactions = msg.reactions || [];
                const reactionIndex = existingReactions.findIndex((r) => r.emoji === wsMsg.emoji);

                let updatedReactions;

                if (reactionIndex >= 0) {
                  updatedReactions = [...existingReactions];

                  updatedReactions[reactionIndex] = {
                    emoji: wsMsg.emoji!,
                    users: wsMsg.userIds!
                  };
                } else {
                  updatedReactions = [
                    ...existingReactions,
                    { emoji: wsMsg.emoji!, users: wsMsg.userIds! }
                  ];
                }

                return { ...msg, reactions: updatedReactions };
              }

              return msg;
            })
          }));
        }
      } catch (error: unknown) {
        console.error('Error parsing WebSocket message:', error);
      }
    },
    [updateMessages, updateStatus]
  );

  // Connect WebSocket
  const connect = useCallback(async () => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      return;
    }

    updateStatus('connecting');

    try {

      const token = await getOrRegisterChatToken();

      chatAuthToken.current = token;
      setCurrentUserId(userIdFromToken(token));

      ws.current = new WebSocket(wsUrl);

      ws.current.addEventListener('open', () => {
        // console.log('WebSocket connected');
        updateStatus('connected');

        // token auth
        setTimeout(() => {
          // console.log('Sending auth...');

          ws.current?.send(JSON.stringify({
            action: 'auth',
            token: token
          }));
        }, 300);

        fetchMessages({ all: true }).catch(err => console.error('Error loading initial messages:', err));

        fetchWatchers().catch(err => console.error('Error loading watchers:', err));

        watchersRefreshInterval.current = setInterval(() => {
          fetchWatchers().catch(err => console.error('Error refreshing watchers:', err));
        }, 30000);

        // Heartbeat every 3 minutes to keep connection on
        heartbeatInterval.current = setInterval(() => {
          if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify({ action: 'ping' }));
          }
        }, 3 * 60 * 1000);
      });

      ws.current.onmessage = handleWebSocketMessage;

      ws.current.addEventListener('error', (error) => {
        console.error('WebSocket error:', error);
        updateStatus('error', 'Error de conexión WebSocket');
      });

      ws.current.addEventListener('close', () => {
        // console.log('WebSocket disconnected');
        updateStatus('disconnected');

        if (heartbeatInterval.current) {
          clearInterval(heartbeatInterval.current);
        }

        if (watchersRefreshInterval.current) {
          clearInterval(watchersRefreshInterval.current);
        }

        // Try to recconect after 3 seconds
        reconnectTimeout.current = setTimeout(() => {
          connect();
        }, 3000);
      });
    } catch (error: unknown) {
      console.error('Error connecting WebSocket:', error);
      updateStatus('error', 'No se pudo conectar al WebSocket');
    }
  }, [wsUrl, adminToken, handleWebSocketMessage, updateStatus]);

  // Timeout user
  const timeoutUser = useCallback(
    async (userId: string, mins: number = 10) => {
      try {
        const url = `${httpEndpoint}/user/${userId}/timeout`;

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${adminToken}`
          },
          body: JSON.stringify({ mins } as TimeoutRequest)
        });

        const data: ApiResponse = await response.json();

        if (data.success) {
          // update watcher locally
          setState((prev) => ({
            ...prev,
            watchers: prev.watchers.map((w) =>
              w.id === userId ? { ...w, timeout_until: Date.now() / 1000 + mins * 60 } : w
            )
          }));
        }

        return data;
      } catch (error: unknown) {
        console.error('Error timing out user:', error);

        const message =
        error instanceof Error ? error.message : 'Unknown error occurred';

        setState((prev) => ({
          ...prev,
          error: message
        }));

        throw error;
      }
    },
    [httpEndpoint, eventId, adminToken]
  );

  // Ban User
  const banUser = useCallback(
    async (userId: string) => {
      try {
        const url = `${httpEndpoint}/user/${userId}/ban`;

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${adminToken}`
          }
        });

        const data: ApiResponse = await response.json();

        if (data.success) {
          // update watcher locally
          setState((prev) => ({
            ...prev,
            watchers: prev.watchers.map((w) =>
              w.id === userId ? { ...w, banned_at: Date.now() / 1000 } : w
            )
          }));

          /*
           * The messages are not touched here: the server broadcasts one delete per item and the
           * socket handler applies them, so doing it locally too would only risk disagreeing.
           */
        }

        return data;
      } catch (error: unknown) {
        console.error('Error banning user:', error);

        const message =
        error instanceof Error ? error.message : 'Unknown error occurred';

        setState((prev) => ({
          ...prev,
          error: message
        }));

        throw error;
      }
    },
    [httpEndpoint, eventId, adminToken]
  );

  // Unban User
  const unbanUser = useCallback(
    async (userId: string) => {
      try {
        const url = `${httpEndpoint}/user/${userId}/unban`;

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${adminToken}`
          }
        });

        /* Asserted through unknown: response.json() is typed `any`, which nothing should inherit. */
        const data = <ApiResponse><unknown> await response.json();

        if (data.success) {
          /* Dropping banned_at rather than zeroing it, to match what the server now stores. */
          setState((prev) => ({
            ...prev,
            watchers: prev.watchers.map((w) =>
              w.id === userId ? { ...w, banned_at: undefined } : w
            )
          }));
        }

        return data;
      } catch (error: unknown) {
        // eslint-disable-next-line no-console -- matches how every other action here reports
        console.error('Error unbanning user:', error);

        const message =
        error instanceof Error ? error.message : 'Unknown error occurred';

        setState((prev) => ({
          ...prev,
          error: message
        }));

        throw error;
      }
    },
    [httpEndpoint, eventId, adminToken]
  );

  /*
   * Delete and restore differ only in the route they call and the flag they set, so they share a
   * body. The local write is what makes the row change immediately; the server's broadcast arrives
   * moments later and says the same thing.
   */
  const setMessageDeleted = useCallback(
    async (messageId: string, replyId: string | undefined, deleted: boolean) => {

    const verb = deleted ? 'remove' : 'restore';

    const endpoint = replyId
      ? `${httpEndpoint}/message/${messageId}/reply/${replyId}/${verb}`
      : `${httpEndpoint}/message/${messageId}/${verb}`;

    try {

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`Error ${deleted ? 'deleting' : 'restoring'} ${replyId ? 'reply' : 'message'}`);
      }

      /* Only moderators reach these routes from here, so the reason is not in doubt. */
      const removedBy: RemovalReason | undefined = deleted ? 'moderator' : undefined;

      setState((prev) => ({
        ...prev,
        messages: prev.messages.map((msg) => {

          if (msg.pk !== messageId) {
            return msg;
          }

          if (replyId) {
            return {
              ...msg,
              replies: msg.replies?.map((r) =>
                r.pk === replyId ? { ...r, deleted, removedBy } : r
              )
            };
          }

          return { ...msg, deleted, removedBy };
        })
      }));

    } catch (error: unknown) {
      console.error(`Error ${deleted ? 'deleting' : 'restoring'}:`, error);

      const message =
        error instanceof Error ? error.message : 'Unknown error occurred';

      setState((prev) => ({
        ...prev,
        error: message
      }));
    }
  },
  [adminToken, httpEndpoint]
);

  const deleteMessage = useCallback(
    (messageId: string, replyId?: string) => {
      void setMessageDeleted(messageId, replyId, true);
    },
    [setMessageDeleted]
  );

  const restoreMessage = useCallback(
    (messageId: string, replyId?: string) => {
      void setMessageDeleted(messageId, replyId, false);
    },
    [setMessageDeleted]
  );


  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [autoConnect, connect, disconnect]);

  return {
    // State
    messages: state.messages,
    watchers: state.watchers,
    status: state.status,
    error: state.error,
    isConnected: state.status === 'connected',
    currentUserId,

    // Actions
    connect,
    disconnect,
    fetchWatchers,
    fetchMessages,
    timeoutUser,
    banUser,
    unbanUser,
    deleteMessage,
    restoreMessage
  };
};
